import apiClient from './apiClient';
import { formatPatientName } from '../utils/patientNameFormatter';
import { formatDicomDate, formatDicomTime, calculateAge } from '../utils/dateTimeFormatter';

/**
 * Library Service - Manages patient/study data from backend API
 */
const libraryService = {
  /**
   * Fetch all studies from backend and transform into patient/study structure
   * GET /api/v1/studies
   * @returns {Promise<{success: boolean, data: Array, error?: string}>}
   */
  async listAccessiblePatients() {
    try {
      const response = await apiClient.get('/studies', {
        params: {
          page_size: 100,
          page: 1,
        }
      });
      // console.log('Raw response from /api/v1/studies:', response);
      const studies = response.data.studies || [];

      // Group studies by patient
      const patientMap = new Map();

      for (const backendStudy of studies) {
        const patientId = backendStudy.patient_id || 'unknown';
        
        if (!patientMap.has(patientId)) {
          patientMap.set(patientId, {
            id: patientId,
            name: formatPatientName(backendStudy.patient_name || 'Unknown'),
            dob: formatDicomDate(backendStudy.patient_birth_date),
            phiSummary: {
              patientId: patientId,
              sex: formatSex(backendStudy.patient_sex),
              age: (() => {
                // Fallback strategy for age
                const dob = backendStudy.patient_birth_date;
                const studyDate = backendStudy.study_date;
                if (dob && studyDate) {
                  const age = calculateAge(dob, studyDate);
                  if (age !== null && !isNaN(age)) return age;
                }
                // Fallback to DICOM PatientAge (e.g., '057Y')
                if (backendStudy.patient_age) {
                  const match = backendStudy.patient_age.match(/(\d{3})[Yy]/);
                  if (match) return parseInt(match[1], 10);
                  // fallback for other formats (e.g., '057Y', '057M', etc.)
                  const num = parseInt(backendStudy.patient_age, 10);
                  if (!isNaN(num)) return num;
                }
                return '';
              })(),
            },
            metadata: {
              mrn: '',
            },
            studies: [],
          });
        }

        const patient = patientMap.get(patientId);
        patient.studies.push({
          id: backendStudy.id,
          description: backendStudy.study_description || 'No Description',
          date: formatDicomDate(backendStudy.study_date),
          time: formatDicomTime(backendStudy.study_time),
          modality: backendStudy.modality || 'MR',
          metadata: {
            studyInstanceUID: backendStudy.study_instance_uid || '',
            studyID: backendStudy.study_id || '',
            accessionNumber: backendStudy.accession_number || '',
            institutionName: backendStudy.institution_name || '',
          },
        });
      }

      const patientsArray = Array.from(patientMap.values());

      return {
        success: true,
        data: patientsArray,
      };
    } catch (error) {
      console.error('Error loading studies from backend:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to load studies from server';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to view studies.';
      } else if (error.response?.status === 404) {
        errorMessage = 'No studies found.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your connection.';
      }

      return {
        success: false,
        data: [],
        error: errorMessage,
      };
    }
  },

  /**
   * Share patient with another user
   */
  async sharePatient(patientId, targetEmail) {
    // TODO: Implement backend sharing
    return {
      success: true,
      message: 'Patient sharing will be implemented with backend',
    };
  },

  /**
   * Share study with another user
   */
  async shareStudy(studyId, targetEmail) {
    // TODO: Implement backend sharing
    return {
      success: true,
      message: 'Study sharing will be implemented with backend',
    };
  },
};

/**
 * Format DICOM sex/gender from backend response
 */
function formatSex(dicomSex) {
  if (!dicomSex || dicomSex === 'U') return 'Unknown';
  
  const sexMap = {
    'M': 'Male',
    'F': 'Female',
    'O': 'Other',
    'U': 'Unknown',
  };
  
  return sexMap[dicomSex.toUpperCase()] || 'Unknown';
}

export default libraryService;